import React from 'react';
import { Key, Laptop, Globe, Layers, Database, Eye, Cog, Shield } from 'lucide-react';

const PILLAR_ICONS = {
  IDENTITY: Key,
  DEVICES: Laptop,
  NETWORKS: Globe,
  APPLICATIONS: Layers,
  DATA: Database,
  VISIBILITY: Eye,
  AUTOMATION: Cog,
  GOVERNANCE: Shield
};

/**
 * CisaPillarSummary - Summary scores displayed below the matrix
 *
 * Shows average maturity score (1-4) for each pillar and cross-cutting capability,
 * visualized as progress bars.
 */
const CisaPillarSummary = ({ pillars, crossCutting, summary }) => {
  const { pillarScores, overallScore, levelCounts, totalFunctions } = summary;

  return (
    <div className="cisa-summary">
      {/* Overall score */}
      <div className="cisa-summary-overall">
        <div className="cisa-summary-overall-label">Overall Maturity</div>
        <div className="cisa-summary-overall-score">
          <span className="cisa-score-value">{overallScore.toFixed(1)}</span>
          <span className="cisa-score-max">/ 4.0</span>
        </div>
        <div className="cisa-summary-overall-bar">
          <div
            className="cisa-summary-overall-fill"
            style={{ width: `${(overallScore / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Pillar scores */}
      <div className="cisa-summary-pillars">
        <div className="cisa-summary-section-title">Pillar Scores</div>
        <div className="cisa-summary-grid">
          {pillars.map(pillar => {
            const score = pillarScores[pillar.code] || 1;
            const Icon = PILLAR_ICONS[pillar.code] || Key;
            return (
              <div key={pillar.code} className="cisa-summary-pillar">
                <div className="cisa-pillar-header">
                  <Icon size={14} style={{ color: pillar.color }} />
                  <span className="cisa-pillar-name">{pillar.name}</span>
                  <span className="cisa-pillar-score">{score.toFixed(1)}</span>
                </div>
                <div className="cisa-pillar-bar">
                  <div
                    className="cisa-pillar-fill"
                    style={{
                      width: `${(score / 4) * 100}%`,
                      backgroundColor: pillar.color
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cross-cutting scores */}
      <div className="cisa-summary-crosscutting">
        <div className="cisa-summary-section-title">Cross-Cutting Capabilities</div>
        <div className="cisa-summary-grid cisa-summary-grid--cc">
          {crossCutting.map(cc => {
            const score = pillarScores[cc.code] || 1;
            const Icon = PILLAR_ICONS[cc.code] || Shield;
            return (
              <div key={cc.code} className="cisa-summary-pillar">
                <div className="cisa-pillar-header">
                  <Icon size={14} style={{ color: 'var(--color-primary)' }} />
                  <span className="cisa-pillar-name">{cc.name}</span>
                  <span className="cisa-pillar-score">{score.toFixed(1)}</span>
                </div>
                <div className="cisa-pillar-bar">
                  <div
                    className="cisa-pillar-fill cisa-pillar-fill--cc"
                    style={{ width: `${(score / 4) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Level distribution */}
      <div className="cisa-summary-distribution">
        <div className="cisa-summary-section-title">Function Distribution</div>
        <div className="cisa-distribution-bars">
          {Object.entries(levelCounts).map(([level, count]) => {
            const pct = totalFunctions > 0 ? (count / totalFunctions) * 100 : 0;
            return (
              <div key={level} className="cisa-distribution-item">
                <span className={`cisa-dist-label cisa-level--${level.toLowerCase()}`}>
                  {level.charAt(0) + level.slice(1).toLowerCase()}
                </span>
                <div className="cisa-dist-bar">
                  <div
                    className={`cisa-dist-fill cisa-level--${level.toLowerCase()}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="cisa-dist-count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CisaPillarSummary;
